import { getApiError } from '@/utils/apiError';

/**
 * Dịch mã lỗi thô của BE thành câu tiếng Việt cho thu ngân.
 *
 * BE ném guard bằng `throw new HttpException('MÃ_LỖI', 400)`, nên `message` trong
 * response CHÍNH LÀ mã lỗi. Trước đây POSPage hiển thị thẳng message đó ⇒ thu
 * ngân nhận đúng chuỗi `PREPAID_EXCEEDS_CUSTOMER_TOTAL` và không biết phải làm gì.
 *
 * Repo CHƯA có bảng ánh xạ mã lỗi BE nào (đã grep: `PAYMENT_SPLIT_MISMATCH` cũng
 * đang hiện thô) — chỉ có `getApiError` lo phần bóc envelope. Bảng dưới đây là
 * lớp dịch đặt lên trên helper đó; thêm mã mới chỉ cần thêm một dòng.
 */

/** Mã lỗi BE → câu hướng dẫn cho thu ngân. */
export const CHECKOUT_ERROR_MESSAGES: Record<string, string> = {
  PREPAID_EXCEEDS_CUSTOMER_TOTAL:
    'Số tiền thu tại quầy đang lớn hơn số khách phải trả. Vui lòng giảm số tiền ở ô "Khách trả trước" cho bằng hoặc nhỏ hơn tổng phải thu rồi tạo đơn lại.',
  // TCR-1176 — đơn bán tại quầy (không giao hàng) thanh toán tiền mặt: BE giờ
  // đối chiếu số tiền thu với tổng đơn phải trả, lệch thì từ chối tạo đơn
  // thay vì âm thầm ghi sai (Phiếu thu sẽ lệch tiền thật).
  CASH_AMOUNT_MISMATCH:
    'Số tiền mặt thu không khớp tổng đơn phải trả. Vui lòng kiểm tra lại tiền khách đưa/tiền thối rồi tạo đơn lại.',
};

/** Mã lỗi BE (đọc từ `error.code`) → câu hướng dẫn. Dùng khi message BE có phần ĐỘNG (vd số tiền cụ thể) nên không thể làm key tra bảng theo message. */
export const CHECKOUT_ERROR_CODES: Record<string, string> = {};

/**
 * Câu hiển thị cho thu ngân khi tạo đơn thất bại.
 *
 * Ưu tiên tra theo `error.code` (bảng `CHECKOUT_ERROR_CODES`) — message ứng với
 * các mã này có phần động (vd "% sàn") nên không dùng làm key được. Mọi lỗi
 * KHÔNG có code trong bảng giữ nguyên đường cũ: message đã chuẩn hoá của BE
 * (tra theo `CHECKOUT_ERROR_MESSAGES`), rồi tới câu mặc định.
 */
export function resolveCheckoutErrorMessage(err: unknown): string {
  const apiError = getApiError(err);
  const code = apiError?.code;
  if (typeof code === 'string' && CHECKOUT_ERROR_CODES[code]) {
    return CHECKOUT_ERROR_CODES[code];
  }
  const normalized = apiError?.message;
  if (!normalized) return 'Thanh toán thất bại';
  return CHECKOUT_ERROR_MESSAGES[normalized] ?? normalized;
}

/**
 * Lỗi tạo đơn có CHỨNG MINH được là đơn chưa vào sổ hay không.
 *
 * `checkoutAttempt` (snapshot khoá tab POS) tồn tại vì đúng MỘT lý do: một lần
 * ghi có thể đã commit ở BE trong khi FE không nhận được kết quả, nên lần bấm
 * sau phải gửi lại y nguyên dữ liệu cũ với cùng idempotency key. Lý do đó chỉ
 * đúng khi kết quả THẬT SỰ không rõ:
 *
 *  - chốt "API server is unavailable" (`ApiUnavailableError` của `services/api`):
 *    request bị từ chối NGAY TẠI interceptor, chưa bao giờ rời trình duyệt;
 *  - 4xx (trừ 408/409): BE đã trả lời và từ chối — không đơn nào được ghi;
 *  - 408 timeout, 409 trùng idempotency key, 5xx, và mọi lỗi KHÔNG có response
 *    (mất mạng, DNS, CORS): đơn có thể đã commit ⇒ PHẢI giữ khoá.
 *
 * Nhận diện `ApiUnavailableError` bằng cờ trên object thay vì import
 * `isApiUnavailableError`: 47 test file mock cả module `@/services/api`, và
 * import từ đó biến mọi test render POSPage thành nạn nhân của mock thiếu
 * named export. `checkoutErrors.test.ts` dựng lỗi bằng CHÍNH class thật nên cờ
 * đổi tên là test đỏ ngay, không trôi lệch âm thầm.
 */
export function proveOrderNotCreated(err: unknown): boolean {
  const unavailable = err as { isApiUnavailableError?: unknown; reachedServer?: unknown };
  if (unavailable?.isApiUnavailableError === true) {
    // Cùng một lớp lỗi cho hai tình huống ngược nhau — chỉ `reachedServer`
    // tách được "chưa gửi đi" khỏi "đã gửi rồi mới đứt mạng".
    return unavailable.reachedServer !== true;
  }
  const status = (err as { response?: { status?: unknown } })?.response?.status;
  if (typeof status !== 'number') return false;
  if (status === 408 || status === 409) return false;
  return status >= 400 && status < 500;
}

/** Mã lỗi BE trả khi suất flash sale hết đúng lúc ghi hoá đơn. */
export const FLASH_SALE_SOLD_OUT_CODE = 'FLASH_SALE_SOLD_OUT';

/**
 * `flash_sale_item_id` của dòng vừa hết suất, hoặc `null` nếu lỗi không phải
 * loại đó.
 *
 * Có id thì POSPage hạ ĐÚNG dòng đó về giá niêm yết và giữ nguyên giỏ. Không
 * có id thì trả `null` — thà báo lỗi chung còn hơn đoán bừa dòng nào rồi hạ
 * giá nhầm một dòng đang bán đúng giá chương trình.
 *
 * Id đọc từ `details` vì `AllExceptionFilter` (shared) dựng lại envelope và
 * chỉ mang theo `code`, `message`, `details`; field lạ ở top-level bị bỏ im
 * lặng.
 */
export function parseFlashSaleSoldOut(err: unknown): string | null {
  const apiError = getApiError<{ flash_sale_item_id?: unknown }>(err);
  if (apiError?.code !== FLASH_SALE_SOLD_OUT_CODE) return null;
  const itemId = apiError.details?.flash_sale_item_id;
  return typeof itemId === 'string' && itemId.length > 0 ? itemId : null;
}
